/* eslint-disable prettier/prettier */
import * as React from "react";
import {
  Modal,
  Grid,
  InputLabel,
  Link,
  Snackbar,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import { Alert } from "@mui/material";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
  KeyboardTimePicker,
} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import CloseIcon from "@mui/icons-material/Close";

// importing app setting
import { AuthenticationContext } from "../../../Context";
import { getHeaders } from "../../../utils/HeaderHelper";
import { useState } from "react";
import GeneralModal from "../../../modals/GeneralModal";
import { isSameDay } from "date-fns";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

// import helpers
import { normalizeWhitespaceForSave } from "../../../utils/WhitespaceHelper";
import { useFlash } from "../../../FlashContext";

interface Props {
  patient_id: number;
  open: boolean;
  labObject: any;
  setOpenAddModal: (boolean) => void;
  onSuccess: (a1cValue: string) => void;
  onModalClose: () => void;
  selectedTestType?: string;
}
// constants
const NOTES_MAX_LENGTH = 500;
const VALUE_MAX_LENGTH = 10;
const LAB_TEST_TYPES = ["A1C", "TC", "HDL"];

const AddLabValues: React.FC<Props> = (props: any) => {
  // authentication context
  const authenticationSetting = React.useContext(AuthenticationContext);
  const labObject = props.labObject;

  // other states
  const { showToast } = useFlash();
  const [error, setError] = useState("");
  const [disabledButton, setDisabledButton] = useState(false);
  const [testType, setTestType] = useState("");
  const [testValue, setTestValue] = useState("");
  const [notes, setNotes] = useState("");
  const [openAddLabValueModal, setOpenAddLabValueModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showSelectedDatePicker, setShowSelectedDatePicker] = useState(false);
  const renderDay = (day, selectedDate, isInCurrentMonth, dayComponent) => {
    const today = new Date();
    const isToday = isSameDay(day, today) && !isSameDay(selectedDate, today);

    return React.cloneElement(dayComponent, {
      style: {
        color: isToday ? "#ff890a" : undefined, // Set color to orange if today
      },
    });
  };

  React.useEffect(() => {
    if (!!labObject) {
      setTestType(labObject.reading_type);
      setTestValue(labObject.reading_value);
      setNotes(labObject.notes);
      setSelectedDate(labObject.date_recorded);
      setOpenAddLabValueModal(true);
    }
  }, [props.labObject]);

  const validForm = () => {
      return Boolean(testType || testValue || selectedDate);
  };

  const handleValueChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.target;
    if (!/^\d*\.?\d*$/.test(value)) return;
    if (value.length > VALUE_MAX_LENGTH) return;
    setTestValue(value);
  };

  React.useEffect(() => {
    if (props?.selectedTestType && props.open) {
      setTestType(props?.selectedTestType);
      if (props?.selectedTestType) {
        setOpenAddLabValueModal(true);
        props.setOpenAddModal(false);
      }
    }
  }, [props?.selectedTestType, props.open]);

  React.useEffect(() => {
    setTestType("");
  }, []);

  const createLabTestRecord = () => {
    setDisabledButton(true);

    if (!validForm()) {
      showToast({ title: "Error", text: "Invalid entries, please check your entries and try again.", type: "error" });
      setDisabledButton(false);
      return;
    }

    const labReading = {
      patient_id: props.patient_id,
      reading_type: testType,
      reading_value: testValue,
      date_recorded: selectedDate,
      notes: normalizeWhitespaceForSave(notes),
    };

    fetch(!!labObject ? `/lab_readings/${labObject.id}` : "/lab_readings", {
      method: labObject ? "PUT" : "POST",
      headers: getHeaders(authenticationSetting.csrfToken),
      body: JSON.stringify({ lab_reading: labReading }),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.error) {
          showToast({ title: "Error", text: result.error, type: "error" });
          setDisabledButton(false);
          return;
        }

        const displayType = testType === "tc" ? "Total Cholesterol" : testType?.toUpperCase();
        const successToast = {
          title: "Success",
          text: `${displayType} has been successfully ${labObject ? "updated" : "added"}.`,
          type: "success"
        };

        sessionStorage.setItem("pendingToast", JSON.stringify(successToast));

        props.onSuccess(result?.resource?.id?.toString());
        props.setOpenAddModal(false);
        setOpenAddLabValueModal(false);
        resetLabObject();
      })
      .catch((error) => {
        showToast({ title: "Error", text: "Something went wrong saving the lab.", type: "error" });
        setDisabledButton(false);
      });
  };

  const handleTestTypeChange = (event) => {
    const type = event.target.value;
    setTestType(type);
    if (type) {
      setOpenAddLabValueModal(true);
      props.setOpenAddModal(false);
    }
  };

  const handleDateChange = (date) => {
    const now = new Date();
    const overLimit =
      date.getDay() == now.getDay() &&
      date.getMonth() == now.getMonth() &&
      date.getFullYear() == now.getFullYear() &&
      date.getTime() > now.getTime();
    if (overLimit) {
      setSelectedDate(now);
    } else {
      setSelectedDate(date);
    }
  };

  const resetLabObject = () => {
    setTestType("");
    setTestValue("");
    setNotes("");
    setSelectedDate(new Date());
    props.onModalClose();
  };

  return (
    <>
      
      <GeneralModal
        open={props.open}
        title={"Add New Lab Value"}
        successCallback={undefined}
        closeCallback={() => props.setOpenAddModal(false)}
        containerClassName="follow-up-modal"
        confirmButtonText="Select"
        width="430px"
      >
        <Grid container style={{ paddingTop: 20 }}>
          <Grid item xs={12} style={{ marginBottom: 10 }}>
            <InputLabel htmlFor="lab_test_type" className="fum-field-label">
              Add new lab value
            </InputLabel>
          </Grid>
          <Grid item xs={12} className="field-container">
            <Select
              fullWidth
              value={testType}
              onChange={handleTestTypeChange}
              placeholder="Select"
            >
              <MenuItem value={""}>Select</MenuItem>
              {LAB_TEST_TYPES.map((testType) => (
                <MenuItem key={testType} value={testType.toLowerCase()}>
                  {testType}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
      </GeneralModal>

      <GeneralModal
        open={openAddLabValueModal}
        title={(!!labObject ? "Edit " : "Add ") + testType?.toUpperCase()}
        successCallback={createLabTestRecord}
        closeCallback={() => {
          setOpenAddLabValueModal(false);
          resetLabObject();
        }}
        containerClassName="follow-up-modal"
        confirmButtonText={!!labObject ? "Update" : "Add"}
        width="430px"
      >
        <Grid
          container
          className="fum-form-container"
          spacing={1}
          style={{ paddingTop: 20 }}
        >
          <Grid item xs={12}>
            <InputLabel htmlFor="value" className="fum-field-label">
              {testType === "tc"
                ? "Total Cholesterol"
                : testType?.toUpperCase()}
            </InputLabel>
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="value"
              size="small"
              placeholder="Enter Value"
              type="text"
              value={testValue}
              className="basic-input-field"
              required
              variant="outlined"
              inputProps={{ maxLength: VALUE_MAX_LENGTH }}
              onChange={handleValueChange}
            />
          </Grid>
          <Grid item xs={12} className="">
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                variant="inline"
                format="MM/dd/yyyy"
                margin="normal"
                renderDay={renderDay}
                id="date-picker-inline"
                label="Date of Collection"
                value={selectedDate}
                open={showSelectedDatePicker}
                onOpen={() => setShowSelectedDatePicker(true)}
                onClose={() => setShowSelectedDatePicker(false)}
                onChange={(date) => {
                  handleDateChange(date);
                  setShowSelectedDatePicker(false);
                }}
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
                maxDate={new Date()}
                style={{ width: "100%" }}
                ToolbarComponent={() => (
                  <Link
                    sx={{
                      fontSize: "14px",
                      order: 1,
                      textAlign: "right",
                      padding: "15px",
                      paddingTop: "0",
                    }}
                    onClick={() => {
                      setSelectedDate(new Date());
                      setShowSelectedDatePicker(false);
                    }}
                  >
                    Clear
                  </Link>
                )}
              />
              <KeyboardTimePicker
                margin="normal"
                id="time-picker"
                label="Time of Collection"
                value={selectedDate}
                onChange={handleDateChange}
                KeyboardButtonProps={{
                  "aria-label": "change time",
                }}
                keyboardIcon={<AccessTimeIcon />}
                style={{ width: "100%" }}
              />
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item xs={12}>
            <InputLabel htmlFor="note" className="fum-field-label">
              Note(s)
            </InputLabel>
          </Grid>
          <Grid item xs={12}>
            <TextField
              id="notes"
              label="Notes"
              value={notes}
              className="basic-input-field"
              multiline
              maxRows={4}
              variant="filled"
              inputProps={{ maxLength: NOTES_MAX_LENGTH }}
              onChange={(
                event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
              ) => {
                const { value } = event.target;
                if (value.length > NOTES_MAX_LENGTH) return;
                setNotes(value);
              }}
            />
          </Grid>
        </Grid>
      </GeneralModal>
    </>
  );
};

export default AddLabValues;
